'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { getAriaLabel, wait } from '@bridge/utils';
import { useFormContext } from 'react-hook-form';
import { Button, Dropzone, DropzoneContent, DropzoneEmptyState, FlexContainer, FormControl, FormDescription, FormItem, FormLabel, FormMessage, LoadingIndicator } from '../../components';
import { Body } from '../../typography/Body';
import { resolveMimeTypeFromFilename } from './uploadConstraints';
import { getFieldIds } from './utils';
import type { DropzoneOptions } from 'react-dropzone';
import type { FieldValues } from 'react-hook-form';
import type { BaseComponentProps, BaseFieldProps } from './types';
import type { UploadConstraints } from './uploadConstraints';

/** Delay between verify poll attempts (ms) */
const VERIFY_POLL_INTERVAL = 500;
/** Total timeout for verify polling (ms) */
const VERIFY_TIMEOUT = 20000;

/**
 * Request payload for fetching an asset upload URL
 */
export interface UploadUrlRequest {
  filename: string;
  contentType: string;
}

/**
 * Response from the asset upload URL endpoint
 */
export interface UploadUrlResponse {
  uploadId: string;
  uploadPost: {
    url: string;
    fields: Record<string, string>;
  };
  expiresAt: string;
  s3Key: string;
}

/**
 * Value stored in the form after a successful upload.
 * Does not include the File object - that's stored internally in the component for preview.
 */
export interface UploadedFileValue {
  uploadId: string;
  s3Key: string;
  /** Original filename, stored for display purposes */
  filename: string;
  /** URL to download/view the uploaded file, available after verification */
  downloadUrl?: string;
}

/**
 * Callback to fetch a presigned upload URL
 */
export type OnUploadCallback = (request: UploadUrlRequest) => Promise<UploadUrlResponse>;

/**
 * Response from verify endpoint
 */
export interface VerifyResponse {
  /** When present, indicates the upload is verified and ready */
  downloadUrl?: string;
}

/**
 * Callback to verify an upload has completed.
 * Should return a response with download_url when the upload is verified.
 */
export type OnVerifyCallback = (uploadId: string) => Promise<VerifyResponse>;

/**
 * Callback to upload a file to S3. Used to proxy uploads through a BFF in development
 * to work around S3 CORS restrictions. In production, uploads go directly to S3.
 */
export type UploadToS3Callback = (url: string, body: FormData) => Promise<Response>;
export type UploadComponentProps = BaseComponentProps<typeof Dropzone> & {
  /**
   * Mime type => extensions map so files of an unsupported type are rejected by the dropzone.
   * Monorail's DeclarativeForm::Upload widget declares this same shape.
   */
  accept?: DropzoneOptions['accept'];
  constraints?: UploadConstraints;
  maxSize?: DropzoneOptions['maxSize'];
  minSize?: DropzoneOptions['minSize'];
  maxFiles?: DropzoneOptions['maxFiles'];
  onError?: (error: Error) => void;
  /** Callback to fetch a presigned S3 upload URL. When provided, enables S3 uploads. */
  onUpload?: OnUploadCallback;
  /** Optional callback to verify an upload has completed after the S3 POST */
  onVerify?: OnVerifyCallback;
  /** Optional callback to notify parent when upload state changes */
  onUploadingChange?: (isUploading: boolean) => void;
  /**
   * Optional callback to upload the file to S3.
   * When provided, uses this callback instead of direct fetch to the presigned URL.
   * Useful for proxying uploads in development to work around CORS restrictions.
   */
  uploadToS3?: UploadToS3Callback;
  /** Label for the remove button in image preview mode. Default: "Remove" */
  removeLabel?: string;
  /** Label for the change button in image preview mode. Default: "Change" */
  changeLabel?: string;
  /**
   * Display-only label for a file uploaded in a prior session (e.g. "File already uploaded").
   * Shown whenever the field has no value of its own, without being written to form state —
   * so an untouched field naturally submits nothing rather than echoing back a placeholder.
   */
  existingUploadLabel?: string;
};
interface UploadFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: UploadComponentProps;
}

/**
 * Returns files for display in the dropzone.
 * Priority: preview files > legacy File[] > placeholder Files from UploadedFileValue > placeholder
 * File from a string value — either a raw string field value, or (more commonly) the
 * `existingUploadLabel` fallback passed in as `rawValue` by the caller below.
 */
function getDisplayFiles(previewFiles: File[], rawValue: File[] | UploadedFileValue[] | string | undefined): File[] {
  if (previewFiles.length > 0) {
    return previewFiles;
  }
  if (!rawValue) {
    return [];
  }
  if (typeof rawValue === 'string') {
    return [new File([], rawValue, {
      type: 'application/octet-stream'
    })];
  }
  if (rawValue.length === 0) {
    return [];
  }
  if (rawValue[0] instanceof File) {
    return rawValue as File[];
  }
  return (rawValue as UploadedFileValue[]).map(uploaded => new File([], uploaded.filename, {
    type: 'application/octet-stream'
  }));
}

/**
 * Polls the verify endpoint until downloadUrl is present or timeout is reached.
 * Waits VERIFY_INITIAL_DELAY before first call, then polls every VERIFY_POLL_INTERVAL.
 * Returns the downloadUrl when verification succeeds.
 */
async function pollVerify(onVerify: OnVerifyCallback, uploadId: string): Promise<string> {
  const deadline = Date.now() + VERIFY_TIMEOUT;
  while (Date.now() < deadline) {
    const result = await onVerify(uploadId);
    if (result.downloadUrl) {
      return result.downloadUrl;
    }
    if (Date.now() + VERIFY_POLL_INTERVAL >= deadline) {
      break;
    }
    await wait(VERIFY_POLL_INTERVAL);
  }
  throw new Error('Upload verification timed out');
}

/**
 * Checks a file against a react-dropzone `accept` map. Matches if the file's mime type
 * (exactly, or by wildcard category) or extension appears anywhere in the map. Used to guard
 * the "change" flow, which selects files via a plain hidden `<input>` that bypasses
 * react-dropzone's own accept-based filtering.
 */
function isFileTypeAccepted(file: File, accept?: DropzoneOptions['accept']): boolean {
  if (!accept) return true;
  const fileType = (file.type || '').toLowerCase();
  const baseType = fileType.replace(/\/.*$/, '');
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  return Object.entries(accept).some(([mime, extensions]) => {
    const normalizedMime = mime.trim().toLowerCase();
    const mimeMatches = normalizedMime.endsWith('/*') ? baseType === normalizedMime.replace(/\/\*$/, '') : fileType === normalizedMime;
    const extensionMatches = extensions.some(ext => fileExtension === ext.trim().toLowerCase());
    return mimeMatches || extensionMatches;
  });
}

/** Returns true if the accept config includes any image MIME types */
function acceptsImages(accept: DropzoneOptions['accept']): boolean {
  if (!accept) return false;
  return Object.keys(accept).some(key => key.startsWith('image/'));
}

/** Converts react-dropzone accept format to an HTML input accept string */
function formatAcceptForInput(accept: DropzoneOptions['accept']): string {
  if (!accept) return '';
  const parts: string[] = [];
  for (const [mime, exts] of Object.entries(accept)) {
    parts.push(mime);
    parts.push(...exts);
  }
  return parts.join(',');
}
function isMimeTypeAccepted(contentType: string, accept: NonNullable<DropzoneOptions['accept']>): boolean {
  return Object.keys(accept).some(allowedType => {
    if (allowedType.endsWith('/*')) {
      return contentType.startsWith(allowedType.slice(0, -1));
    }
    return allowedType === contentType;
  });
}
function resolveAcceptedContentType(file: File, constraints: UploadConstraints | undefined, accept: DropzoneOptions['accept']): string | undefined {
  if (!constraints && !accept) {
    return file.type || 'application/octet-stream';
  }
  const contentType = file.type.toLowerCase();
  const effectiveAccept = accept ?? constraints?.accept;
  if (contentType && effectiveAccept && isMimeTypeAccepted(contentType, effectiveAccept) && (!constraints || constraints.mimeTypes.includes(contentType))) {
    return contentType;
  }
  if (constraints) {
    const resolvedContentType = resolveMimeTypeFromFilename(file.name, constraints);
    if (resolvedContentType && effectiveAccept && isMimeTypeAccepted(resolvedContentType, effectiveAccept)) {
      return resolvedContentType;
    }
    return undefined;
  }
  const extensionStart = file.name.lastIndexOf('.');
  if (extensionStart < 0 || !effectiveAccept) return undefined;
  const extension = file.name.slice(extensionStart).toLowerCase();
  return Object.entries(effectiveAccept).find(([mimeType, extensions]) => !mimeType.endsWith('/*') && extensions.some(allowedExtension => allowedExtension.toLowerCase() === extension))?.[0];
}
interface UploadPreviewProps {
  previewUrl: string;
  removeLabel: string;
  changeLabel: string;
  onRemove: () => void;
  onChangeClick: () => void;
  disabled: boolean;
}
function UploadPreview(t0) {
  const $ = _c(16);
  const {
    previewUrl,
    removeLabel,
    changeLabel,
    onRemove,
    onChangeClick,
    disabled
  } = t0;
  let t1;
  if ($[0] !== previewUrl) {
    t1 = <img className="h-12 w-auto object-contain" alt="Uploaded preview" src={previewUrl} />;
    $[0] = previewUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== onRemove || $[4] !== removeLabel) {
    t2 = <Button disabled={disabled} onClick={onRemove} size="sm" type="button" variant="quaternary">{removeLabel}</Button>;
    $[2] = disabled;
    $[3] = onRemove;
    $[4] = removeLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== changeLabel || $[7] !== disabled || $[8] !== onChangeClick) {
    t3 = <Button disabled={disabled} onClick={onChangeClick} size="sm" type="button" variant="secondary">{changeLabel}</Button>;
    $[6] = changeLabel;
    $[7] = disabled;
    $[8] = onChangeClick;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <FlexContainer className="gap-2" items="center">{t2}{t3}</FlexContainer>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t4) {
    t5 = <FlexContainer className="w-full border border-dashed border-border-secondary rounded-md p-3" items="center" justify="between">{t1}{t4}</FlexContainer>;
    $[13] = t1;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
export function UploadField<TFieldValues extends FieldValues>({
  field,
  fieldConfig,
  fieldState,
  componentProps,
  labelSize
}: UploadFieldProps<TFieldValues>) {
  const {
    id,
    disabled: componentDisabled,
    accept,
    constraints,
    maxSize,
    minSize,
    maxFiles,
    onError,
    onUpload,
    onVerify,
    onUploadingChange,
    uploadToS3,
    removeLabel = 'Remove',
    changeLabel = 'Change',
    existingUploadLabel,
    ...restComponentProps
  } = componentProps;
  const {
    setError
  } = useFormContext<TFieldValues>();
  const effectiveAccept = accept ?? constraints?.accept;
  const effectiveMaxSize = maxSize ?? constraints?.maxSize;
  const [isUploading, setIsUploading] = useState(false);
  // Store files internally for preview display (not in form value)
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDisabled = field.disabled || componentDisabled || isUploading;
  const {
    fieldId,
    labelId,
    descriptionId,
    errorId,
    describedBy,
    isInvalid
  } = getFieldIds(fieldConfig, fieldState, id);
  const isImageUpload = acceptsImages(effectiveAccept);
  const rawValue = field.value as File[] | UploadedFileValue[] | string | undefined ?? existingUploadLabel;
  const hasUploadedContent = !!rawValue && Array.isArray(rawValue) && rawValue.length > 0;
  const showImagePreview = !isUploading && hasUploadedContent && isImageUpload && !!previewUrl;

  // Clear preview files when the form value is reset externally (e.g., form.reset())
  useEffect(() => {
    if (!field.value || Array.isArray(field.value) && field.value.length === 0) {
      setPreviewFiles([]);
      setPreviewUrl(null);
    }
  }, [field.value]);

  // Manage preview URL: prefer downloadUrl from form value, fall back to object URL from file
  useEffect(() => {
    if (!hasUploadedContent || !isImageUpload) {
      setPreviewUrl(null);
      return;
    }

    // Check for downloadUrl on UploadedFileValue
    const firstValue = rawValue![0];
    if (!(firstValue instanceof File) && (firstValue as UploadedFileValue).downloadUrl) {
      setPreviewUrl((firstValue as UploadedFileValue).downloadUrl!);
      return;
    }

    // Fall back to object URL from preview file
    const file = previewFiles[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [hasUploadedContent, isImageUpload, rawValue, previewFiles]);

  // For display: use preview files if available, otherwise fall back to legacy File[] format or create placeholder Files from UploadedFileValue
  const files = getDisplayFiles(previewFiles, rawValue);

  /**
   * Handles file drop. If onUpload is provided, fetches presigned URLs and uploads to S3.
   * Otherwise, falls back to legacy behavior of storing files directly.
   */
  const handleDrop = async (droppedFiles: File[]) => {
    // Guard against the "change" flow, which selects a file via a plain hidden <input> that
    // bypasses react-dropzone's own accept-based filtering (the main Dropzone already filters
    // droppedFiles by accept before calling this).
    const rejectedFiles = droppedFiles.filter(file_0 => !isFileTypeAccepted(file_0, accept));
    if (rejectedFiles.length > 0) {
      onError?.(new Error(`Unsupported file type: ${rejectedFiles.map(file_1 => file_1.name).join(', ')}`));
    }
    const acceptedFiles = droppedFiles.filter(file_2 => isFileTypeAccepted(file_2, accept));
    if (acceptedFiles.length === 0) return;

    // Legacy behavior: store files directly if no upload callback
    if (!onUpload) {
      field.onChange(acceptedFiles);
      return;
    }
    setIsUploading(true);
    onUploadingChange?.(true);
    try {
      const uploadedFiles: UploadedFileValue[] = [];
      const uploadedPreviewFiles: File[] = [];
      for (const file_3 of acceptedFiles) {
        const contentType = resolveAcceptedContentType(file_3, constraints, effectiveAccept);
        if (!contentType) {
          setError(field.name, {
            type: 'validate',
            message: 'Unsupported file type'
          });
          continue;
        }

        // 1. Get presigned URL from server
        const response = await onUpload({
          filename: file_3.name,
          contentType
        });

        // 2. Upload file to S3 using presigned URL (via proxy in dev, direct in prod)
        const body = new FormData();
        for (const [key, value] of Object.entries(response.uploadPost.fields)) {
          body.append(key, value);
        }
        body.append('file', file_3);
        const uploadResponse = uploadToS3 ? await uploadToS3(response.uploadPost.url, body) : await fetch(response.uploadPost.url, {
          method: 'POST',
          body
        });
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
        }

        // 3. Optionally verify the upload with polling
        let downloadUrl: string | undefined;
        if (onVerify) {
          downloadUrl = await pollVerify(onVerify, response.uploadId);
        }

        // 4. Store metadata and commit incrementally to prevent orphaned S3 files
        const uploadedFile: UploadedFileValue = {
          uploadId: response.uploadId,
          s3Key: response.s3Key,
          filename: file_3.name,
          downloadUrl
        };
        uploadedFiles.push(uploadedFile);
        uploadedPreviewFiles.push(file_3);

        // Commit each file incrementally to the form state
        // This ensures that if a later file fails, earlier files are already saved
        setPreviewFiles([...uploadedPreviewFiles]);
        field.onChange([...uploadedFiles]);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : (error as {
        message?: string;
      })?.message || 'Upload failed';
      onError?.(new Error(message));
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
    }
  };
  const handleRemove = () => {
    setPreviewFiles([]);
    setPreviewUrl(null);
    field.onChange([]);
  };
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleDrop(Array.from(selectedFiles));
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };
  return <FormItem className="flex-1">
      {fieldConfig.label && <FormLabel htmlFor={fieldId} id={labelId} required={fieldConfig.required} size={labelSize} weight="medium">
          {fieldConfig.label}
        </FormLabel>}
      <FormControl>
        <div className="relative">
          {showImagePreview ? <>
              <UploadPreview changeLabel={changeLabel} disabled={isDisabled} onChangeClick={() => fileInputRef.current?.click()} onRemove={handleRemove} previewUrl={previewUrl} removeLabel={removeLabel} />
              <input className="hidden" accept={formatAcceptForInput(effectiveAccept)} onChange={handleFileInputChange} ref={fileInputRef} type="file" />
            </> : <Dropzone {...restComponentProps} className="border-dashed" accept={effectiveAccept} aria-describedby={describedBy} aria-invalid={isInvalid} aria-label={getAriaLabel(fieldConfig.label)} disabled={isDisabled} files={files} id={fieldId} maxFiles={maxFiles} maxSize={effectiveMaxSize} minSize={minSize} onDrop={handleDrop} onError={onError}>
              <DropzoneEmptyState />
              <DropzoneContent />
            </Dropzone>}
          {isUploading && <FlexContainer className="absolute inset-0 bg-background-primary/80 rounded-md whitespace-normal" direction="column" items="center" justify="center">
              <LoadingIndicator size="lg" />
              <Body className="mt-2 text-content-secondary" size="sm">
                Uploading...
              </Body>
            </FlexContainer>}
        </div>
      </FormControl>
      {fieldConfig.description && <FormDescription className="text-content-secondary" id={descriptionId}>
          {fieldConfig.description}
        </FormDescription>}
      {fieldState.error?.message && <FormMessage id={errorId}>{fieldState.error.message}</FormMessage>}
    </FormItem>;
}